import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { LogEntryCard } from "../components/tag/LogEntryCard";
import { ScoreSubmissions } from "../components/tag/ScoreSubmissions";
import { InterviewSubmissions } from "../components/tag/InterviewSubmissions";
import { Analytics } from "../components/tag/Analytics";
import { ArticlesAdmin } from "../components/tag/ArticlesAdmin";
import { ChangeKeyPanel } from "../components/tag/ChangeKeyPanel";
import { LeadMagnetsAdmin } from "../components/tag/LeadMagnetsAdmin";
import { api } from "../lib/api";

const ADMIN_KEY_STORAGE = "tag_admin_key";
const ADMIN_TAB_STORAGE = "tag_admin_tab";

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(ADMIN_KEY_STORAGE));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ headline: "", content: "", tags: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState(() => sessionStorage.getItem(ADMIN_TAB_STORAGE) || "log");
  const [challenge, setChallenge] = useState(null); // { id, sent_to }
  const [code, setCode] = useState("");

  const switchTab = (next) => {
    setTab(next);
    sessionStorage.setItem(ADMIN_TAB_STORAGE, next);
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/log");
      setEntries(data || []);
    } catch {
      toast.error("Failed to load entries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadEntries();
  }, [authed, loadEntries]);

  const verifyAndSignIn = async (e) => {
    e.preventDefault();
    if (!adminKey.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/admin/login", { key: adminKey }, { validateStatus: () => true });
      if (res.status === 401) { toast.error("Invalid admin key."); return; }
      if (res.status < 200 || res.status >= 300) { toast.error("Could not start sign-in."); return; }
      if (res.data.needs_code) {
        setChallenge({ id: res.data.challenge_id, sent_to: res.data.sent_to });
        toast.message("Code sent — check your inbox.");
        return;
      }
      sessionStorage.setItem(ADMIN_KEY_STORAGE, adminKey);
      setAuthed(true);
      toast.success("Signed in.");
    } catch {
      toast.error("Could not verify key.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/admin/login/verify-code", { challenge_id: challenge.id, code: code.trim() }, { validateStatus: () => true });
      if (res.status >= 200 && res.status < 300) {
        sessionStorage.setItem(ADMIN_KEY_STORAGE, adminKey);
        setAuthed(true);
        setChallenge(null);
        setCode("");
        toast.success("Signed in.");
      } else {
        toast.error(res.data?.detail || "Code rejected.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = () => {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey("");
    setAuthed(false);
    setEntries([]);
  };

  const createEntry = async (e) => {
    e.preventDefault();
    if (!form.headline.trim() || !form.content.trim()) {
      toast.error("Headline and content are required.");
      return;
    }
    setSubmitting(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const res = await api.post(
        "/log",
        { headline: form.headline.trim(), content: form.content.trim(), tags },
        { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true }
      );
      if (res.status === 401) {
        toast.error("Admin key rejected. Sign in again.");
        signOut();
        return;
      }
      if (res.status >= 200 && res.status < 300) {
        toast.success(`Entry #${res.data.entry_number} published.`);
        setForm({ headline: "", content: "", tags: "" });
        loadEntries();
      } else {
        toast.error("Failed to publish entry.");
      }
    } catch {
      toast.error("Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Admin | The Authority Gap" path="/admin">
      <section style={{ background: "#090909", padding: "60px 20px 80px" }}>
        <div style={{ maxWidth: authed ? 1200 : 760, margin: "0 auto" }}>
          {!authed ? (
            challenge ? (
              <div className="tag-card" data-testid="admin-2fa-card" style={{ maxWidth: 460, margin: "40px auto", padding: 32 }}>
                <h1 className="tag-h2" style={{ marginBottom: 8 }}>Enter your code</h1>
                <p style={{ color: "#aaa", fontSize: 13, marginBottom: 24 }}>
                  We sent a 6-digit code to <strong style={{ color: "#fff" }}>{challenge.sent_to}</strong>. Valid for 10 minutes.
                </p>
                <form onSubmit={verifyCode}>
                  <input
                    data-testid="admin-2fa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    className="tag-input"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                    style={{ marginBottom: 14, fontSize: 24, letterSpacing: "0.4em", textAlign: "center" }}
                  />
                  <button data-testid="admin-2fa-submit" type="submit" className="btn-primary" style={{ width: "100%" }} disabled={submitting || code.length !== 6}>
                    {submitting ? "Verifying…" : "Sign in"}
                  </button>
                  <button type="button" onClick={() => { setChallenge(null); setCode(""); }} style={{ background: "transparent", border: "none", color: "#777", fontSize: 11, marginTop: 12, cursor: "pointer", width: "100%" }}>
                    ← Use a different key
                  </button>
                </form>
              </div>
            ) : (
            <div className="tag-card" data-testid="admin-signin-card" style={{ maxWidth: 460, margin: "40px auto", padding: 32 }}>
              <h1 className="tag-h2" style={{ marginBottom: 8 }}>Admin sign in</h1>
              <p style={{ color: "#aaa", fontSize: 13, marginBottom: 24 }}>
                Enter your admin credentials to manage The Authority Gap.
              </p>
              <form onSubmit={verifyAndSignIn}>
                <label style={{ display: "block", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Username</label>
                <input
                  data-testid="admin-username"
                  type="text"
                  className="tag-input"
                  value="Admin"
                  readOnly
                  style={{ marginBottom: 14, opacity: 0.7 }}
                />
                <label style={{ display: "block", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Key</label>
                <input
                  data-testid="admin-key-input"
                  type="password"
                  className="tag-input"
                  placeholder="Your admin key"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  autoFocus
                  style={{ marginBottom: 14 }}
                />
                <button
                  data-testid="admin-signin-submit"
                  type="submit"
                  className="btn-primary"
                  style={{ width: "100%" }}
                  disabled={submitting || !adminKey.trim()}
                >
                  {submitting ? "Verifying…" : "Sign in"}
                </button>
              </form>
            </div>
            )
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h1 className="tag-h2" style={{ marginBottom: 4 }}>TAG Admin</h1>
                  <p style={{ color: "#aaa", fontSize: 13, margin: 0 }}>
                    Manage Builder's Log and the warm-lead pipeline.
                  </p>
                </div>
                <button
                  data-testid="admin-signout"
                  type="button"
                  onClick={signOut}
                  className="btn-ghost"
                >
                  Sign out
                </button>
              </div>

              {/* Tabs */}
              <div
                data-testid="admin-tabs"
                style={{
                  display: "flex",
                  gap: 4,
                  borderBottom: "1px solid #1a1a1a",
                  marginBottom: 28,
                }}
              >
                {[
                  { id: "log", label: "Builder's Log" },
                  { id: "scores", label: "Score Submissions" },
                  { id: "interviews", label: "Interview Submissions" },
                  { id: "articles", label: "Articles" },
                  { id: "magnets", label: "Lead Magnets" },
                  { id: "analytics", label: "Analytics" },
                  { id: "settings", label: "Settings" },
                ].map((t) => {
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`admin-tab-${t.id}`}
                      type="button"
                      onClick={() => switchTab(t.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        borderBottom: `2px solid ${active ? "#FF6575" : "transparent"}`,
                        padding: "10px 18px",
                        color: active ? "#fff" : "#888",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: "var(--font-body)",
                        cursor: "pointer",
                        marginBottom: -1,
                        transition: "color 0.15s ease",
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {tab === "log" && (
                <>
                  {/* Composer */}
                  <div className="tag-card" data-testid="admin-composer" style={{ marginBottom: 32 }}>
                    <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>New entry</h2>
                    <form onSubmit={createEntry}>
                      <label style={{ display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Headline</label>
                      <input
                        data-testid="admin-headline"
                        type="text"
                        className="tag-input"
                        placeholder="What did you build / ship / learn?"
                        value={form.headline}
                        onChange={(e) => setForm({ ...form, headline: e.target.value })}
                        style={{ marginBottom: 14 }}
                      />
                      <label style={{ display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Content</label>
                      <textarea
                        data-testid="admin-content"
                        rows={6}
                        className="tag-input"
                        placeholder="The honest story. Plain text or markdown."
                        value={form.content}
                        onChange={(e) => setForm({ ...form, content: e.target.value })}
                        style={{ marginBottom: 14, resize: "vertical", minHeight: 120, fontFamily: "var(--font-body)" }}
                      />
                      <label style={{ display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tags (comma-separated)</label>
                      <input
                        data-testid="admin-tags"
                        type="text"
                        className="tag-input"
                        placeholder="e.g. health, launch, framework"
                        value={form.tags}
                        onChange={(e) => setForm({ ...form, tags: e.target.value })}
                        style={{ marginBottom: 18 }}
                      />
                      <button
                        data-testid="admin-publish"
                        type="submit"
                        className="btn-primary"
                        disabled={submitting}
                      >
                        {submitting ? "Publishing…" : "Publish entry"}
                      </button>
                    </form>
                  </div>

                  {/* Existing entries */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
                    <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>
                      Published entries{entries.length > 0 ? ` (${entries.length})` : ""}
                    </h2>
                    <button
                      data-testid="admin-refresh"
                      type="button"
                      onClick={loadEntries}
                      style={{ background: "transparent", border: "none", color: "#777", fontSize: 12, cursor: "pointer" }}
                    >
                      ↻ Refresh
                    </button>
                  </div>

                  {loading && (
                    <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>
                  )}
                  {!loading && entries.length === 0 && (
                    <div className="tag-card" style={{ textAlign: "center", color: "#bbb" }}>
                      No entries yet. Publish the first one above.
                    </div>
                  )}
                  {!loading && entries.map((e) => <LogEntryCard key={e.id || e.entry_number} entry={e} />)}
                </>
              )}

              {tab === "scores" && (
                <ScoreSubmissions adminKey={adminKey} onUnauthorized={signOut} />
              )}

              {tab === "interviews" && (
                <InterviewSubmissions adminKey={adminKey} onUnauthorized={signOut} />
              )}

              {tab === "analytics" && (
                <Analytics adminKey={adminKey} onUnauthorized={signOut} />
              )}

              {tab === "articles" && (
                <ArticlesAdmin adminKey={adminKey} onUnauthorized={signOut} />
              )}

              {tab === "magnets" && (
                <LeadMagnetsAdmin adminKey={adminKey} onUnauthorized={signOut} />
              )}

              {tab === "settings" && (
                <ChangeKeyPanel
                  adminKey={adminKey}
                  onChanged={(newKey) => {
                    sessionStorage.setItem(ADMIN_KEY_STORAGE, newKey);
                    setAdminKey(newKey);
                  }}
                  onUnauthorized={signOut}
                />
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
